import React from 'react'
import { LOCAL_STORAGE_KEY, LOGIN_URL } from '../constants/auth'

interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: 'Bearer'
}
export interface UserInfo {
  sub: string
  'custom:clientId': string
  email: string
  exp: number // Timestamp de expiración
}

export interface AuthState {
  isLoggedIn: boolean
  loading: boolean
  session?: TokenResponse
  userInfo?: UserInfo
  error?: boolean
  loginUrl: string
  signOut: () => void
}

const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.')
    const payload = JSON.parse(atob(parts[1]))

    const currentTime = Math.floor(Date.now() / 1000)
    return payload.exp < currentTime
  } catch (error) {
    console.error('Error decodificando token:', error)
    return true
  }
}

const refreshTokens = async (refreshToken: string): Promise<TokenResponse> => {
  const payload: Record<string, string> = {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: process.env.REACT_APP_COGNITO_CLIENT_ID!,
  }

  const formBody = Object.keys(payload)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`)
    .join('&')

 const response = await fetch(`${process.env.REACT_APP_COGNITO_CLIENT_DOMAIN}/oauth2/token`, {
    method: 'POST',
    body: formBody,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })

  if (!response.ok) {
    throw new Error(await response.text())
  }

  const tokens = await response.json()

  // Si no devuelve nuevo refresh_token, mantener el anterior
  if (!tokens.refresh_token) {
    tokens.refresh_token = refreshToken
  }

  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tokens))
  return tokens
}

const signIn = async (code: string): Promise<TokenResponse> => {
  const payload: Record<string, string> = {
    grant_type: 'authorization_code',
    code,
    client_id: process.env.REACT_APP_COGNITO_CLIENT_ID!,
    redirect_uri: process.env.REACT_APP_COGNITO_REDIRECT_URI!,
  }

  const formBody = Object.keys(payload)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`)
    .join('&')

  try {
    const response = await fetch(`${process.env.REACT_APP_COGNITO_CLIENT_DOMAIN}/oauth2/token`, {
      method: 'post',
      body: formBody,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    if (!response.ok) {
      throw new Error(await response.text())
    }

    const tokens = await response.json()

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tokens))

    if (window.history.pushState) {
      const newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname
      window.history.pushState({ path: newUrl }, '', newUrl)
    }

    return tokens
  } catch (error) {
    console.error('Error signing in: ', error)
    throw error
  }
}

const AuthContext = React.createContext<AuthState>({
  isLoggedIn: false,
  loading: false,
  error: false,
  loginUrl: '',
  signOut: () => {},
})

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [session, setSession] = React.useState<TokenResponse>()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)
  const [userInfo, setUserInfo] = React.useState<UserInfo>()

  const signOut = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
    setSession(undefined)
    setUserInfo(undefined)
    window.location.href = '/'
  }

  React.useEffect(() => {
    if (session?.id_token) {
      const parts = session.id_token.split('.')
      setUserInfo(JSON.parse(atob(parts[1])))
    }
  }, [session])

  React.useEffect(() => {
    if (session || loading || error) {
      return
    }
    setLoading(true)
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')

    if (code) {
      signIn(code)
        .then(tokens => {
          setSession(tokens)
        })
        .catch(e => {
          setError(true)
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      // Si no hay código, intentar recuperar la sesión del localStorage
      const storedSession = localStorage.getItem(LOCAL_STORAGE_KEY)

      if (storedSession) {
        try {
          const parsedSession = JSON.parse(storedSession)

          if (isTokenExpired(parsedSession.id_token)) {
            if (parsedSession.refresh_token) {
              refreshTokens(parsedSession.refresh_token)
                .then(newTokens => {
                  setSession(newTokens)
                })
                .catch(e => {
                  signOut()
                })
                .finally(() => {
                  setLoading(false)
                })
            } else {
              signOut()
              setLoading(false)
            }
          } else {
            setSession(parsedSession)
            setLoading(false)
          }
        } catch (e) {
          console.error('Error parsing stored session', e)
          localStorage.removeItem(LOCAL_STORAGE_KEY)
        }
      }

      setLoading(false)
    }
  }, [loading, session, error])

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!session,
        session,
        userInfo,
        loading,
        error,
        loginUrl: LOGIN_URL,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = () => React.useContext(AuthContext)
